import { useEffect, useState } from "react";
import { X, AlertTriangle, FileWarning } from "lucide-react";
import { api } from "@/lib/api";
import { CENS_STATES, CENSUS_TO_UI, SYNC_PENDING } from "@/components/CensimentoFigure";

// PEND-PW-01 (GO §6): vista esplicativa della completeness_basis — "Perché questa %?".
// Legge SOLO /api/census/{organ} (canonical). Business-first: MAI JSON grezzo all'utente.
const FACT_STATE_LABELS = {
  verified: "Verificato",
  declared: "Dichiarato",
  present_unverified: "Presente, da verificare",
  inconsistent: "Incoerente",
  missing: "Mancante",
};
const FACT_STATE_COLORS = {
  verified: "#10B981", declared: "#38BDF8", present_unverified: "#F59E0B",
  inconsistent: "#EF4444", missing: "#6B7280",
};
const NA_BASIS_LABELS = {
  nessun_contratto_attivo_noto: "Nessun contratto attivo censito",
  nessuna_polizza_nota: "Nessuna polizza nota",
  nessun_documento_acquisito: "Nessun documento acquisito",
  dichiarazione_registrata_nessun_dipendente: "Dichiarazione registrata: nessun dipendente",
  canale_dichiarato_non_desiderato: "Canale dichiarato non desiderato",
  canale_dichiarato_non_presente: "Canale dichiarato non presente",
};
const humanKey = (k) => (k || "").replaceAll("_", " ");
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function Section({ title, testid, children }) {
  return (
    <div className="mt-4" data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] text-gray-500 uppercase mb-2">{title}</p>
      {children}
    </div>
  );
}

export default function CensusBasisPanel({ organId, organName, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    api.get(`/census/${organId}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [organId]);

  const basis = data?.completeness_basis || {};
  const st = data ? (CENS_STATES[CENSUS_TO_UI[data.census_state]] || SYNC_PENDING) : SYNC_PENDING;
  const cng = basis.censita_not_granted;
  const entries = basis.entries || [];
  const na = basis.excluded_not_applicable || [];
  const expectedRefs = basis.open_expected_refs || [];
  const anomalies = data?.anomalies || [];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="cens-basis-overlay" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" style={{ backdropFilter: "blur(6px)" }} />
      <div className="relative glass rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[86vh] overflow-y-auto p-5"
        data-testid="cens-basis-panel" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] tracking-[0.25em] text-gray-500 uppercase">Perché questa percentuale?</p>
            <h3 className="font-display text-lg font-bold text-gray-100 mt-1">{organName || organId}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="cens-basis-close" aria-label="Chiudi"
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-cyan-300 shrink-0"
            style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)" }}>
            <X size={14} />
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-400 mt-4" data-testid="cens-basis-error">
            Base di calcolo non disponibile per questa area.
          </p>
        )}
        {!data && !error && <p className="text-sm text-gray-500 mt-4">Caricamento…</p>}

        {data && (
          <>
            <div className="flex items-center gap-3 mt-4">
              <span className="chip" style={{ color: st.color, borderColor: `${st.color}35`, background: `${st.color}10` }}
                data-testid="cens-basis-state">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} />
                {st.label}
              </span>
              <span className="font-display text-2xl font-bold" style={{ color: st.color }}
                data-testid="cens-basis-coverage">
                {data.coverage_pct == null ? "—" : `${data.coverage_pct}%`}
              </span>
              {cng && (
                <span className="text-[10px] text-amber-400 font-medium" data-testid="cens-basis-qualified">
                  copertura qualificata
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-500 mt-2" data-testid="cens-basis-meta">
              Aggiornato: {fmtDate(data.computed_at)}
              {basis.catalog_version ? ` · Catalogo ${basis.catalog_version}` : ""}
              {basis.perimeter_version ? ` · Perimetro ${basis.perimeter_version}` : ""}
            </p>

            <Section title="Come è calcolata" testid="cens-basis-formula">
              <p className="text-[11px] text-gray-400 leading-relaxed">
                {basis.applicable_count ?? 0} requisiti applicabili nel perimetro
                {na.length ? ` (${na.length} non applicabili esclusi, con motivazione)` : ""}.
                Ogni requisito ha un peso: la percentuale è la somma dei pesi raggiunti sul totale.
              </p>
            </Section>

            {entries.length > 0 && (
              <Section title="Requisiti nel perimetro" testid="cens-basis-entries">
                <ul className="space-y-1.5">
                  {entries.map((e) => (
                    <li key={e.catalog_entry_id} className="flex items-center gap-2 text-[11px]">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: FACT_STATE_COLORS[e.state] || "#6B7280" }} />
                      <span className="text-gray-300 flex-1 min-w-0 truncate">{humanKey(e.fact_key)}</span>
                      <span className="shrink-0" style={{ color: FACT_STATE_COLORS[e.state] || "#6B7280" }}>
                        {FACT_STATE_LABELS[e.state] || e.state}
                      </span>
                      <span className="text-gray-600 shrink-0">peso {e.weight}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {na.length > 0 && (
              <Section title="Non applicabili (e perché)" testid="cens-basis-na">
                <ul className="space-y-1.5">
                  {na.map((n) => (
                    <li key={n.catalog_entry_id} className="text-[11px] text-gray-400 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-gray-600" />
                      <span>{humanKey(n.fact_key)} — <span className="text-gray-500">{NA_BASIS_LABELS[n.basis] || n.basis}</span></span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {cng && (
              <Section title="Cosa blocca lo stato «Censita»" testid="cens-basis-blockers">
                <div className="rounded-xl p-3 border border-amber-400/20 bg-amber-400/5">
                  <div className="flex items-center gap-2 text-amber-400 text-[11px] font-semibold mb-1.5">
                    <AlertTriangle size={12} /> {cng.reason}
                  </div>
                  <ul className="space-y-1">
                    {(cng.entries || []).map((b) => (
                      <li key={b.catalog_entry_id} className="text-[11px] text-gray-400">
                        {humanKey(b.fact_key)}
                        {b.gap_refs?.length ? <span className="text-gray-600"> · riferimento {b.gap_refs.join(", ")}</span> : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              </Section>
            )}

            {expectedRefs.length > 0 && (
              <Section title="Documenti attesi" testid="cens-basis-expected">
                <ul className="space-y-1.5">
                  {expectedRefs.map((e, i) => (
                    <li key={e.expected_id || i} className="text-[11px] text-gray-400 flex items-start gap-2">
                      <FileWarning size={12} className="text-amber-400 shrink-0 mt-0.5" />
                      <span>{humanKey(e.expected_document_type)}{e.reason ? ` — ${e.reason}` : ""}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {anomalies.length > 0 && (
              <Section title="Da verificare" testid="cens-basis-anomalies">
                <ul className="space-y-1.5">
                  {anomalies.map((a, i) => (
                    <li key={i} className="text-[11px] text-gray-400 flex items-start gap-2">
                      <AlertTriangle size={12} className="text-red-400 shrink-0 mt-0.5" />
                      <span>{a.message}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <p className="text-[9px] text-gray-600 mt-5 leading-relaxed border-t border-white/5 pt-3">
              Fonte: censimento (area_states canonico). La percentuale misura quanto il sistema conosce
              quest'area sul perimetro di catalogo indicato — non la salute del business.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
